import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { ApiError, createReference, deleteReference, listReference, updateReference } from '../../lib/api';
import { SparkInput } from '../SparkInput';
import { SparkMessage } from '../SparkMessage';

const PAGE = 50;

export interface Column<T> {
  header: string;
  render: (item: T) => ReactNode;
  mono?: boolean;
}

/** What a form can do with its draft. */
export interface FormProps<D> {
  draft: D;
  set: (patch: Partial<D>) => void;
  errors: Record<string, string>;
  /** True when editing an item already on file: its code is fixed. */
  editing: boolean;
  disabled: boolean;
}

export interface ResourceTabProps<T, D> {
  /** The API resource: "airports", "fare-rules". */
  resource: string;
  /** Singular, lower case: "airport". */
  noun: string;
  searchPlaceholder: string;
  hint?: ReactNode;
  columns: Column<T>[];
  keyOf: (item: T) => string;
  /** The item's address for PUT and DELETE. */
  pathOf: (item: T) => string;
  labelOf: (item: T) => string;
  emptyDraft: () => D;
  toDraft: (item: T) => D;
  /** The request body, or field errors found before sending. */
  toBody: (draft: D) => { body: unknown } | { errors: Record<string, string> };
  Form: (props: FormProps<D>) => ReactNode;
  /** Items that cannot be edited, only created and deleted. */
  createOnly?: boolean;
  deleteWarning?: string;
  wide?: boolean;
}

/** A searchable, paged reference table with a create/edit dialog. */
export function ResourceTab<T, D>(props: ResourceTabProps<T, D>) {
  const { resource, noun, columns, keyOf, pathOf, labelOf } = props;

  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState<{ item: T | null } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      try {
        const result = await listReference<T>(resource, { search, limit: PAGE, offset }, signal);
        setItems(result.items);
        setTotal(result.total);
        setError('');
      } catch (err) {
        if (signal?.aborted) return;
        setError(err instanceof ApiError ? err.message : 'The list could not be loaded.');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [resource, search, offset],
  );

  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => void load(controller.signal), 250);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  const handleDelete = async (item: T) => {
    const warning = props.deleteWarning ? `\n\n${props.deleteWarning}` : '';
    if (!window.confirm(`Delete ${noun} ${labelOf(item)}?${warning}`)) return;
    setBusy(keyOf(item));
    setError('');
    try {
      await deleteReference(resource, pathOf(item));
      setNotice(`Deleted ${noun} ${labelOf(item)}.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `The ${noun} could not be deleted.`);
    } finally {
      setBusy(null);
    }
  };

  const handleSaved = (message: string) => {
    setEditing(null);
    setNotice(message);
    void load();
  };

  const last = Math.min(offset + PAGE, total);

  return (
    <>
      <div className="toolbar">
        <SparkInput
          label="Search"
          placeholder={props.searchPlaceholder}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setOffset(0);
          }}
        />
        <button
          type="button"
          className="spark-btn spark-btn--md spark-btn--primary"
          onClick={() => {
            setNotice('');
            setEditing({ item: null });
          }}
        >
          Add {noun}
        </button>
      </div>

      {props.hint && <p className="ref-hint">{props.hint}</p>}
      {error && <SparkMessage variant="error">{error}</SparkMessage>}
      {notice && <SparkMessage variant="success">{notice}</SparkMessage>}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.header}>{c.header}</th>
              ))}
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="table__empty">
                  {loading ? 'Loading…' : search ? 'Nothing matches the search.' : `No ${noun} on file yet.`}
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={keyOf(item)}>
                {columns.map((c) => (
                  <td key={c.header} className={c.mono ? 'mono' : undefined}>
                    {c.render(item)}
                  </td>
                ))}
                <td>
                  <div className="table__actions">
                    {!props.createOnly && (
                      <button
                        type="button"
                        className="spark-btn-link"
                        disabled={busy === keyOf(item)}
                        onClick={() => {
                          setNotice('');
                          setEditing({ item });
                        }}
                      >
                        Edit
                      </button>
                    )}
                    <button
                      type="button"
                      className="spark-btn-link"
                      style={{ color: 'var(--sedl-red-70)' }}
                      disabled={busy === keyOf(item)}
                      onClick={() => void handleDelete(item)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > PAGE && (
        <div className="ref-pager">
          <span>
            {offset + 1}–{last} of {total}
          </span>
          <button
            type="button"
            className="spark-btn spark-btn--md spark-btn--secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
          >
            Previous
          </button>
          <button
            type="button"
            className="spark-btn spark-btn--md spark-btn--secondary"
            disabled={last >= total}
            onClick={() => setOffset(offset + PAGE)}
          >
            Next
          </button>
        </div>
      )}

      {editing && (
        <EditDialog {...props} item={editing.item} onClose={() => setEditing(null)} onSaved={handleSaved} />
      )}
    </>
  );
}

interface EditDialogProps<T, D> extends ResourceTabProps<T, D> {
  item: T | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

function EditDialog<T, D>({ item, onClose, onSaved, ...props }: EditDialogProps<T, D>) {
  const [draft, setDraft] = useState<D>(() => (item ? props.toDraft(item) : props.emptyDraft()));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const editing = item !== null;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    const result = props.toBody(draft);
    if ('errors' in result) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      if (item) {
        await updateReference(props.resource, props.pathOf(item), result.body);
        onSaved(`Saved ${props.noun} ${props.labelOf(item)}.`);
      } else {
        await createReference(props.resource, result.body);
        onSaved(`Added the ${props.noun}.`);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.message);
        setErrors(err.fields ?? {});
      } else {
        setFormError(`The ${props.noun} could not be saved.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = item ? `Edit ${props.noun} ${props.labelOf(item)}` : `Add ${props.noun}`;

  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`dialog${props.wide ? ' dialog--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ref-dialog-title"
      >
        <div className="dialog__header">
          <h2 className="dialog__title" id="ref-dialog-title">
            {title}
          </h2>
          <button type="button" className="dialog__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="dialog__body">
            {formError && <SparkMessage variant="error">{formError}</SparkMessage>}
            <props.Form
              draft={draft}
              set={(patch) => setDraft((current) => ({ ...current, ...patch }))}
              errors={errors}
              editing={editing}
              disabled={submitting}
            />
          </div>
          <div className="dialog__footer">
            <button
              type="button"
              className="spark-btn spark-btn--md spark-btn--secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="spark-btn spark-btn--md spark-btn--primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
